import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckBadgeIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import api from '../../api/axiosConfig';

const VerifyInvoice = () => {
  const { id: invoiceNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await api.get(`/invoices/verify/${invoiceNumber}`);
        setInvoice(res.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Verification Failed');
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [invoiceNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 max-w-md w-full text-center relative overflow-hidden">
        
        {/* Background Accent */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>

        <div className="relative z-10">
          <div className="mb-6 flex justify-center">
            {invoice ? (
              <div className="relative">
                <div className="absolute inset-0 bg-green-500 blur-xl opacity-30 rounded-full"></div>
                <CheckBadgeIcon className="h-24 w-24 text-green-500 relative z-10" />
              </div>
            ) : (
              <div className="relative">
                <div className="absolute inset-0 bg-red-500 blur-xl opacity-30 rounded-full"></div>
                <XCircleIcon className="h-24 w-24 text-red-500 relative z-10" />
              </div>
            )}
          </div>

          <h1 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">
            {invoice ? 'Verified Authentic' : 'Verification Failed'}
          </h1>
          
          <p className="text-gray-500 mb-8 font-medium">
            {invoice 
              ? 'This document was securely generated and verified by our system.'
              : error}
          </p>

          {invoice && (
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 text-left space-y-4 shadow-inner">
              
              <div className="flex items-center gap-3 text-purple-600 mb-4 pb-4 border-b border-gray-200">
                <DocumentTextIcon className="h-6 w-6" />
                <span className="font-bold tracking-wider">{invoice.invoiceNumber}</span>
              </div>

              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">Issued By</p>
                <p className="text-gray-900 font-bold">{invoice.businessName}</p>
                {invoice.businessPhone && (
                  <p className="text-gray-500 text-sm mt-0.5">{invoice.businessPhone}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">Issue Date</p>
                  <p className="text-gray-900 font-bold">
                    {new Date(invoice.issueDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Amount</p>
                  <p className="text-gray-900 font-extrabold text-lg text-purple-600">
                    ₹{invoice.totalAmount?.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8">
            <Link 
              to="/"
              className="inline-block text-sm font-bold text-purple-600 hover:text-purple-700 underline underline-offset-4"
            >
              Powered by Khatabook
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyInvoice;
